import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPallet, placePallet, movePallet, qcHoldPallet, qcClearPallet, voidPallet, issuePallet, splitPallet, mergePallet, getPalletLabel, reprintPalletLabel, getPutawaySuggestion, getAllBoxLabels } from '@/lib/api';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { SlotPicker } from '@/components/SlotPicker';
import { Package, MapPin, ArrowRight, Shield, Trash2, Truck, Move, QrCode, Clock, Box, FileText, AlertTriangle, Printer, Split, Merge, RotateCcw } from 'lucide-react';
import { CATEGORY_COLORS, CATEGORY_LABELS, STATUS_COLORS, STATUS_LABELS, ROW_COLORS } from '@/lib/constants';

export default function PalletDetailPage() {
  const { palletId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pallet, setPallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [placeSlot, setPlaceSlot] = useState('');
  const [moveSlot, setMoveSlot] = useState('');
  const [qcReason, setQcReason] = useState('');
  const [qcAction, setQcAction] = useState('');
  const [qcOutcome, setQcOutcome] = useState('approved');
  const [showPlace, setShowPlace] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [showQcHold, setShowQcHold] = useState(false);
  const [showQcClear, setShowQcClear] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [splitBoxIds, setSplitBoxIds] = useState([]);
  const [mergeSourceId, setMergeSourceId] = useState('');
  const [putawaySuggestion, setPutawaySuggestion] = useState(null);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { loadPallet(); }, [palletId]);

  const loadPallet = async () => {
    setLoading(true);
    try {
      const res = await getPallet(palletId);
      setPallet(res.data);
    } catch (err) {
      toast.error('Pallet not found');
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  };

  const handlePlace = async () => {
    if (!placeSlot) return toast.error('Enter slot address');
    try {
      await placePallet(pallet.id, { slot_address: placeSlot.toUpperCase(), scan_method: 'manual' });
      toast.success(`Placed at ${placeSlot.toUpperCase()}`);
      setShowPlace(false);
      loadPallet();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleMove = async () => {
    if (!moveSlot) return toast.error('Enter destination');
    try {
      await movePallet(pallet.id, { to_slot: moveSlot.toUpperCase(), scan_method: 'manual' });
      toast.success(`Moved to ${moveSlot.toUpperCase()}`);
      setShowMove(false);
      loadPallet();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleQcHold = async () => {
    if (!qcReason) return toast.error('Enter reason');
    try {
      await qcHoldPallet(pallet.id, { reason: qcReason });
      toast.success('QC Hold placed');
      setShowQcHold(false);
      loadPallet();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleQcClear = async () => {
    try {
      await qcClearPallet(pallet.id, { action_taken: qcAction, outcome: qcOutcome });
      toast.success('QC Hold cleared');
      setShowQcClear(false);
      loadPallet();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleVoid = async () => {
    if (!window.confirm('Void this pallet? This action cannot be undone.')) return;
    try {
      await voidPallet(pallet.id);
      toast.success('Pallet voided');
      loadPallet();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAdminHardDelete = async () => {
    const ref = pallet.pallet_ref;
    const typed = window.prompt(
      `ADMIN HARD DELETE\n\nThis permanently removes pallet ${ref} and all its items, boxes and transactions.\nThe reference ${ref} will be recycled and used for the next new pallet.\n\nType DELETE to confirm:`
    );
    if (typed !== 'DELETE') {
      if (typed !== null) toast.error('Confirmation text did not match. Cancelled.');
      return;
    }
    try {
      const res = await adminHardDeletePallet(pallet.id);
      const d = res.data;
      toast.success(`${ref} deleted${d.recycled ? ' — ref recycled' : ''}`);
      navigate('/inventory');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = detail
        ? `Hard delete failed: ${detail}`
        : status === 404
          ? 'Hard delete failed: backend route not found (production may need a redeploy)'
          : status
            ? `Hard delete failed (HTTP ${status})`
            : `Hard delete failed: ${err.message || 'network error'}`;
      toast.error(msg);
    }
  };

  const handlePrintLabel = async () => {
    try {
      const res = await getPalletLabel(pallet.pallet_ref);
      window.open(`${API_BASE}${res.data.url}`, '_blank');
      toast.success(`Label v${res.data.label_version}`);
    } catch (err) { toast.error('Failed to generate label'); }
  };

  const handleReprintLabel = async () => {
    try {
      const res = await reprintPalletLabel(pallet.pallet_ref);
      window.open(`${API_BASE}${res.data.url}`, '_blank');
      toast.success(`Reprinted as Label v${res.data.label_version}`);
      loadPallet();
    } catch (err) { toast.error('Failed'); }
  };

  const handlePrintBoxLabels = async () => {
    try {
      const res = await getAllBoxLabels(pallet.id);
      if (res.data.labels && res.data.labels.length > 0) {
        // Open each box label
        for (const label of res.data.labels) {
          window.open(`${API_BASE}${label.url}`, '_blank');
        }
        toast.success(`${res.data.count} box label(s) generated`);
      } else {
        toast.error('No boxes found for this pallet');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate box labels'); }
  };

  const handleSplit = async () => {
    if (splitBoxIds.length === 0) return toast.error('Select boxes to split');
    try {
      const res = await splitPallet(pallet.id, { box_ids: splitBoxIds });
      toast.success(`Split complete — new pallet: ${res.data.new_pallet_ref}`);
      setShowSplit(false);
      setSplitBoxIds([]);
      navigate(`/pallet/${res.data.new_pallet_ref}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleMerge = async () => {
    if (!mergeSourceId) return toast.error('Enter source pallet ref');
    try {
      await mergePallet(pallet.id, { source_pallet_id: mergeSourceId });
      toast.success('Merge complete');
      setShowMerge(false);
      loadPallet();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-[#1a2fce] border-t-transparent rounded-full" /></div>;
  if (!pallet) return null;

  const totalPcs = pallet.items?.reduce((s, i) => s + (i.qty_pcs_total || 0), 0) || 0;
  const totalNet = pallet.items?.reduce((s, i) => s + (i.qty_weight_net_kg || 0), 0) || 0;
  const totalGross = pallet.items?.reduce((s, i) => s + (i.qty_weight_gross_kg || 0), 0) || 0;
  const categories = [...new Set(pallet.items?.map(i => i.category) || [])];

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="pallet-detail">
      {/* QC Hold Banner */}
      {pallet.status === 'qc_hold' && (
        <div className="bg-red-600 text-white p-4 rounded-xl flex items-center gap-3" data-testid="qc-hold-banner">
          <AlertTriangle size={24} />
          <div>
            <div className="font-bold">QC HOLD — This pallet is under quality investigation</div>
            <div className="text-sm text-red-100">Issue/Dispatch is disabled. Reason: {pallet.qc_hold_reason}</div>
          </div>
        </div>
      )}

      {/* Void Banner */}
      {pallet.status === 'void' && (
        <div className="bg-gray-800 text-white p-4 rounded-xl flex items-center gap-3">
          <Trash2 size={24} />
          <div className="font-bold">VOIDED — This pallet has been voided</div>
        </div>
      )}

      {/* Header Card */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-3xl font-mono font-bold text-[#0d1f6e]">{pallet.pallet_ref}</div>
            <Badge className="text-xs px-2 py-1 text-white" style={{ backgroundColor: STATUS_COLORS[pallet.status] }}>
              {STATUS_LABELS[pallet.status]}
            </Badge>
          </div>
          {pallet.slot_address && (
            <div className="flex items-center gap-1.5 mb-3 cursor-pointer hover:opacity-80" onClick={() => navigate('/map')} data-testid="pallet-location">
              <MapPin size={14} className="text-[#1a2fce]" />
              <span className="font-mono font-semibold text-[#1a2fce]">{pallet.slot_address}</span>
              <span className="text-xs text-gray-400">· Row {pallet.slot_address[0]}</span>
            </div>
          )}
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>{totalPcs.toLocaleString()} pcs</span>
            <span>Net: {totalNet.toFixed(1)} kg</span>
            <span>Gross: {totalGross.toFixed(1)} kg</span>
          </div>
          {pallet.space_score > 0 && (
            <div className="text-xs text-amber-600 font-medium mt-1">Space Score: {pallet.space_score} · {pallet.days_in_warehouse} days</div>
          )}
          {/* Category dots */}
          <div className="flex gap-1.5 mt-2">
            {categories.map((c) => (
              <div key={c} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-white font-medium" style={{ backgroundColor: CATEGORY_COLORS[c] }}>
                {CATEGORY_LABELS[c]}
              </div>
            ))}
          </div>
          {pallet.label_version > 1 && <div className="text-xs text-gray-400 mt-1">Label v{pallet.label_version}</div>}
          {pallet.is_return && <Badge className="mt-1 text-xs bg-orange-500 text-white border-0">RETURN — Challan: {pallet.original_challan_ref}</Badge>}
          {pallet.split_from && <div className="text-xs text-gray-400 mt-1">Split from another pallet</div>}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {pallet.status === 'ready' && (
          <Dialog open={showPlace} onOpenChange={setShowPlace}>
            <DialogTrigger asChild><Button className="h-9 text-xs font-semibold" style={{ backgroundColor: '#1a2fce' }} data-testid="place-btn"><MapPin size={14} className="mr-1" /> Place</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Place Pallet</DialogTitle></DialogHeader>
              <div className="space-y-3"><Label>Slot Address</Label><SlotPicker value={placeSlot} onChange={setPlaceSlot} placeholder="Search slot... e.g. B03-L2" data-testid="place-slot-input" />
              <Button onClick={handlePlace} className="w-full h-10" style={{ backgroundColor: '#0e7a45' }} data-testid="confirm-place">Confirm Placement</Button></div>
            </DialogContent>
          </Dialog>
        )}
        {['placed', 'partial'].includes(pallet.status) && (
          <>
            <Dialog open={showMove} onOpenChange={setShowMove}>
              <DialogTrigger asChild><Button variant="outline" className="h-9 text-xs" data-testid="move-btn"><Move size={14} className="mr-1" /> Move</Button></DialogTrigger>
              <DialogContent><DialogHeader><DialogTitle>Move Pallet</DialogTitle></DialogHeader>
                <div className="space-y-3"><Label>Destination</Label><SlotPicker value={moveSlot} onChange={setMoveSlot} placeholder="Search destination..." />
                <Button onClick={handleMove} className="w-full h-10" style={{ backgroundColor: '#1a2fce' }}>Confirm Move</Button></div>
              </DialogContent>
            </Dialog>
            {pallet.status !== 'qc_hold' && (
              <Button onClick={() => navigate(`/issue/${pallet.id}`)} className="h-9 text-xs font-semibold" style={{ backgroundColor: '#d97706' }} data-testid="issue-btn">
                <Truck size={14} className="mr-1" /> Issue / Dispatch
              </Button>
            )}
          </>
        )}
        {['placed', 'partial'].includes(pallet.status) && ['manager', 'admin'].includes(user?.role) && (
          <Dialog open={showQcHold} onOpenChange={setShowQcHold}>
            <DialogTrigger asChild><Button variant="outline" className="h-9 text-xs border-red-300 text-red-600" data-testid="qc-hold-btn"><Shield size={14} className="mr-1" /> QC Hold</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Place QC Hold</DialogTitle></DialogHeader>
              <div className="space-y-3"><Label>Reason (required)</Label><Input value={qcReason} onChange={e => setQcReason(e.target.value)} placeholder="e.g. Salt spray test pending" />
              <Button onClick={handleQcHold} className="w-full h-10 bg-red-600">Confirm QC Hold</Button></div>
            </DialogContent>
          </Dialog>
        )}
        {pallet.status === 'qc_hold' && ['manager', 'admin'].includes(user?.role) && (
          <Dialog open={showQcClear} onOpenChange={setShowQcClear}>
            <DialogTrigger asChild><Button className="h-9 text-xs bg-green-600" data-testid="qc-clear-btn"><Shield size={14} className="mr-1" /> Clear QC Hold</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Clear QC Hold</DialogTitle></DialogHeader>
              <div className="space-y-3"><Label>Action Taken</Label><Input value={qcAction} onChange={e => setQcAction(e.target.value)} placeholder="e.g. Test passed" />
              <Label>Outcome</Label>
              <select value={qcOutcome} onChange={e => setQcOutcome(e.target.value)} className="w-full h-9 border rounded px-2 text-sm">
                <option value="approved">Approved - Return to stock</option><option value="rejected">Rejected - Void</option><option value="rework">Rework</option>
              </select>
              <Button onClick={handleQcClear} className="w-full h-10" style={{ backgroundColor: '#0e7a45' }}>Clear Hold</Button></div>
            </DialogContent>
          </Dialog>
        )}
        {user?.role === 'admin' && !['void', 'issued'].includes(pallet.status) && (
          <Button onClick={handleVoid} variant="outline" className="h-9 text-xs border-red-300 text-red-600" data-testid="void-btn">
            <Trash2 size={14} className="mr-1" /> Void
          </Button>
        )}
        {user?.role === 'admin' && (
          <Button
            onClick={handleAdminHardDelete}
            variant="outline"
            className="h-9 text-xs border-red-600 text-red-700 bg-red-50 hover:bg-red-100"
            data-testid="admin-hard-delete-btn"
            title="Permanently delete pallet and recycle its reference number"
          >
            <Trash2 size={14} className="mr-1" /> Force Delete (Admin)
          </Button>
        )}
        {/* Labels */}
        {!['void'].includes(pallet.status) && (
          <>
            <Button onClick={handlePrintLabel} variant="outline" className="h-9 text-xs" data-testid="print-label-btn">
              <Printer size={14} className="mr-1" /> Print Label
            </Button>
            <Button onClick={handleReprintLabel} variant="outline" className="h-9 text-xs" data-testid="reprint-label-btn">
              <Printer size={14} className="mr-1" /> Reprint (v+1)
            </Button>
            <Button onClick={handlePrintBoxLabels} variant="outline" className="h-9 text-xs" data-testid="print-box-labels-btn" style={{ borderColor: '#d97706', color: '#d97706' }}>
              <Box size={14} className="mr-1" /> Print Box Labels
            </Button>
          </>
        )}
        {/* Split */}
        {['placed', 'partial', 'packing'].includes(pallet.status) && pallet.boxes?.length > 1 && (
          <Dialog open={showSplit} onOpenChange={setShowSplit}>
            <DialogTrigger asChild><Button variant="outline" className="h-9 text-xs" data-testid="split-btn"><Split size={14} className="mr-1" /> Split</Button></DialogTrigger>
            <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Split Pallet — Select Boxes</DialogTitle></DialogHeader>
              <p className="text-xs text-gray-500 mb-2">Selected boxes will move to a new pallet. Both labels will be reprinted.</p>
              <div className="space-y-1">
                {pallet.boxes?.map(box => (
                  <label key={box.id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${splitBoxIds.includes(box.id) ? 'bg-[#1a2fce]/10 border border-[#1a2fce]' : 'bg-gray-50 hover:bg-gray-100'}`}>
                    <input type="checkbox" checked={splitBoxIds.includes(box.id)}
                      onChange={e => {
                        if (e.target.checked) setSplitBoxIds([...splitBoxIds, box.id]);
                        else setSplitBoxIds(splitBoxIds.filter(id => id !== box.id));
                      }} className="rounded" />
                    <div>
                      <div className="font-mono text-xs font-bold">{box.box_ref}</div>
                      <div className="text-[10px] text-gray-500">{box.qty_pcs} pcs · {box.weight_net_kg} kg</div>
                    </div>
                  </label>
                ))}
              </div>
              <Button onClick={handleSplit} className="w-full h-10 mt-2" style={{ backgroundColor: '#1a2fce' }} data-testid="confirm-split">
                Split {splitBoxIds.length} Box{splitBoxIds.length !== 1 ? 'es' : ''} to New Pallet
              </Button>
            </DialogContent>
          </Dialog>
        )}
        {/* Merge */}
        {['placed', 'partial', 'packing'].includes(pallet.status) && (
          <Dialog open={showMerge} onOpenChange={setShowMerge}>
            <DialogTrigger asChild><Button variant="outline" className="h-9 text-xs" data-testid="merge-btn"><Merge size={14} className="mr-1" /> Merge</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Merge Into This Pallet</DialogTitle></DialogHeader>
              <p className="text-xs text-gray-500 mb-2">Source pallet contents will be merged here. Source will be voided.</p>
              <div className="space-y-3">
                <div><Label className="text-xs">Source Pallet ID</Label><Input value={mergeSourceId} onChange={e => setMergeSourceId(e.target.value)} placeholder="Enter source pallet ID" className="h-9 text-sm" /></div>
                <Button onClick={handleMerge} className="w-full h-10" style={{ backgroundColor: '#1a2fce' }} data-testid="confirm-merge">Confirm Merge</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Items */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Items ({pallet.items?.length || 0})</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {pallet.items?.map((item) => (
            <div key={item.item_code || item.id} className="border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-8 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[item.category] }} />
                <div className="flex-1">
                  <div className="font-semibold text-sm">{item.description}</div>
                  <div className="text-xs text-gray-500">{item.item_code} · {CATEGORY_LABELS[item.category]}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 ml-4">
                {item.size && <div>Size: <span className="font-medium">{item.size}</span></div>}
                {item.grade && <div>Grade: <span className="font-medium">{item.grade}</span></div>}
                {item.standard && <div>Standard: <span className="font-medium">{item.standard}</span></div>}
                {item.finish && <div>Finish: <span className="font-medium">{item.finish}</span></div>}
                <div>Qty: <span className="font-medium">{item.qty_pcs_total?.toLocaleString()} pcs</span></div>
                <div>Net: <span className="font-medium">{item.qty_weight_net_kg} kg</span></div>
                <div>Gross: <span className="font-medium">{item.qty_weight_gross_kg} kg</span></div>
                <div>Cartons: <span className="font-medium">{item.ctns_count}</span></div>
                {item.batch_no && <div>Batch: <span className="font-medium">{item.batch_no}</span></div>}
                {item.heat_no && <div>Heat: <span className="font-medium">{item.heat_no}</span></div>}
                {item.customer?.name && <div className="col-span-2">Customer: <span className="font-medium italic">{item.customer.name}</span></div>}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Boxes */}
      {pallet.boxes?.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Boxes ({pallet.boxes.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {pallet.boxes.map((box) => (
                <div key={box.box_ref || box.id} className={`p-2 rounded-lg border text-xs ${box.is_exception ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`}>
                  <div className="font-mono font-semibold">{box.box_ref}</div>
                  <div className="text-gray-500">{box.qty_pcs} pcs · {box.weight_net_kg} kg</div>
                  <Badge variant="outline" className="text-[9px] mt-1">{box.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Movement History */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Movement History</CardTitle></CardHeader>
        <CardContent>
          {pallet.transactions?.length > 0 ? (
            <div className="space-y-2">
              {pallet.transactions.map((t) => (
                <div key={t.id || t.timestamp} className="flex items-start gap-3 text-xs py-1.5 border-b border-gray-50 last:border-0">
                  <Clock size={12} className="text-gray-400 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-700">{t.type?.replace(/_/g, ' ')}</div>
                    {t.from_slot && <span className="text-gray-400">{t.from_slot} → {t.to_slot}</span>}
                    {t.notes && <div className="text-gray-400">{t.notes}</div>}
                  </div>
                  <span className="text-gray-400 shrink-0">{t.timestamp ? new Date(t.timestamp).toLocaleString() : ''}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-400 text-sm">No movement history</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
